import { NgKLoggerService } from '@kin/ngk-logger';
import { patchState, signalStoreFeature, type, withMethods } from '@ngrx/signals';

import type { LegacyApiPagesComponent, LegacyQuotePagesPath } from 'app/home/common/services/legacy/quote-pages';
import { mapLegacyPathToFormKey } from 'app/home/create-quote/form-config/map-legacy-path-to-form-key';
import { getComponentsForLegacyPagesApiAboutYou } from 'app/home/create-quote/forms/about-you/about-you-utils';
import { getOptionsForLegacyPagesApiCoApplicantComponent } from 'app/home/create-quote/forms/co-applicant/co-applicant-utils';
import { getComponentsForLegacyPagesApiCoApplicantInfo } from 'app/home/create-quote/forms/co-applicant-info/co-applicant-info.utils';
import { getComponentsForLegacyPagesApiInsuranceExpiration } from 'app/home/create-quote/forms/insurance-expiration/insurance-expiration.utils';
import { getOptionsForLegacyPagesApiInsuranceStatusComponent } from 'app/home/create-quote/forms/insurance-status/insurance-status-utils';
import { InsuranceStatusRadioButtonOption } from 'app/home/create-quote/forms/insurance-status/insurance-status.types';
import { getComponentsForLegacyPagesApiPolicyStartDate } from 'app/home/create-quote/forms/policy-start-date/policy-start-date-utils';
import { getOptionsForLegacyPagesApiPolicyTypeComponent } from 'app/home/create-quote/forms/policy-type/policy-type-utils';
import { PolicyTypeRadioButtonOption } from 'app/home/create-quote/forms/policy-type/policy-type.types';
import { ConstructionTypeRadioButtonOption } from 'app/home/create-quote/forms/property-details/construction-type/construction-type.types';
import { getOptionsForLegacyPagesApiConstructionTypeComponent } from 'app/home/create-quote/forms/property-details/construction-type/construction-type.utils';
import { getOptionsForLegacyPagesApiExteriorWallComponent } from 'app/home/create-quote/forms/property-details/exterior-wall/exterior-wall.utils';
import { FoundationTypeRadioButtonOption } from 'app/home/create-quote/forms/property-details/foundation/foundation.types';
import { getOptionsForLegacyPagesApiFoundationTypeComponent } from 'app/home/create-quote/forms/property-details/foundation/foundation.utils';
import { getOptionsForLegacyPagesApiPropertyTypeComponent } from 'app/home/create-quote/forms/property-details/property-type/property-type-utils';
import { PropertyTypeRadioButtonOption } from 'app/home/create-quote/forms/property-details/property-type/property-type.types';
import { getOptionsForLegacyPagesApiRoofShapeComponent } from 'app/home/create-quote/forms/property-details/roof-shape/roof-shape-utils';
import { RoofShapeRadioButtonOption } from 'app/home/create-quote/forms/property-details/roof-shape/roof-shape.types';
import { getValidationsForLegacyPagesAPIRoofYearComponent } from 'app/home/create-quote/forms/property-details/roof-year/roof-year-utils';
import { RoofYearTypeValidations } from 'app/home/create-quote/forms/property-details/roof-year/roof-year.types';
import { YearBuiltFormOption } from 'app/home/create-quote/forms/property-details/year-built/year-built.types';
import { getOptionsForLegacyPagesApiYearBuiltComponent } from 'app/home/create-quote/forms/property-details/year-built/year-built.utils';
import { getOptionsForLegacyPagesApiPropertyInformationComponent } from 'app/home/create-quote/forms/property-information/property-information.utils';
import { getOptionsForLegacyPagesApiRentalNumberComponent } from 'app/home/create-quote/forms/rental-number/rental-number-utils';
import { RentalNumberSelectOption } from 'app/home/create-quote/forms/rental-number/rental-number.types';
import { getOptionsForLegacyPagesApiRentalStatusComponent } from 'app/home/create-quote/forms/rental-status/rental-status-utils';
import { getOptionsForLegacyPagesApiResidencyComponent } from 'app/home/create-quote/forms/residency/residency-utils';
import { ResidencyRadioButtonOption } from 'app/home/create-quote/forms/residency/residency.types';
import { getOptionsForLegacyPagesApiUnitNumberCheckComponent } from 'app/home/create-quote/forms/unit-number-check/unit-number-check-utils';
import { getOptionsForLegacyPagesApiUnitNumberEntryComponent } from 'app/home/create-quote/forms/unit-number-entry/unit-number-entry-utils';
import { quoteFormOptionsInitialState } from 'app/home/global-store/quote-form-options/quote-form-options-initial-state';
import { QuoteFormOptionsState } from 'app/home/global-store/quote-form-options/quote-form-options.model';

type LegacyOptions =
  | PolicyTypeRadioButtonOption[]
  | InsuranceStatusRadioButtonOption[]
  | RentalNumberSelectOption[]
  | ResidencyRadioButtonOption[]
  | PropertyTypeRadioButtonOption[]
  | RoofShapeRadioButtonOption[]
  | YearBuiltFormOption[]
  | ConstructionTypeRadioButtonOption[]
  | FoundationTypeRadioButtonOption[]
  | RoofYearTypeValidations;

export function withQuoteFormOptionsMethods() {
  return signalStoreFeature(
    { state: type<QuoteFormOptionsState>() },
    withMethods((store) => ({
      reset(initialState?: QuoteFormOptionsState): void {
        patchState(store, () => initialState ?? quoteFormOptionsInitialState);
      },
      updateOptionsForFormByLegacyPath(legacyPath: LegacyQuotePagesPath, options: LegacyOptions | undefined, logger: NgKLoggerService): void {
        const formKey = mapLegacyPathToFormKey(legacyPath);
        if (!formKey) {
          logger.error({ message: `unable to map legacy path to form key: ${formKey}`, context: 'updateOptionsForFormByLegacyPath', priority: 'P3' });
          return;
        }
        patchState(store, () => ({ [formKey]: options }));
      },
      getLegacyOptionsForForm(legacyPath: LegacyQuotePagesPath, components: LegacyApiPagesComponent[] | undefined, logger: NgKLoggerService): LegacyOptions | undefined {
        const formKey = mapLegacyPathToFormKey(legacyPath);
        if (!formKey) {
          logger.error({ message: `unable to map legacy path to form key: ${formKey}`, context: 'getLegacyOptionsForForm', priority: 'P3' });
          return;
        }
        if (!components) {
          logger.error({ message: `components for formKey ${formKey} is undefined`, context: 'getLegacyOptionsForForm', priority: 'P3' });
          return;
        }

        switch (formKey) {
          case 'about-you':
            return getComponentsForLegacyPagesApiAboutYou(components, logger);
          case 'policy-type':
            return getOptionsForLegacyPagesApiPolicyTypeComponent(components, logger);
          case 'unit-number-check':
            return getOptionsForLegacyPagesApiUnitNumberCheckComponent(components, logger);
          case 'unit-number-entry':
            return getOptionsForLegacyPagesApiUnitNumberEntryComponent(components, logger);
          case 'insurance-status':
            return getOptionsForLegacyPagesApiInsuranceStatusComponent(components, logger);
          case 'rental-status':
            return getOptionsForLegacyPagesApiRentalStatusComponent(components, logger);
          case 'rental-number':
            return getOptionsForLegacyPagesApiRentalNumberComponent(components, logger);
          case 'residency':
            return getOptionsForLegacyPagesApiResidencyComponent(components, logger);
          case 'policy-start-date':
            return getComponentsForLegacyPagesApiPolicyStartDate(components, logger);
          case 'co-applicant':
            return getOptionsForLegacyPagesApiCoApplicantComponent(components, logger);
          case 'co-applicant-info':
            return getComponentsForLegacyPagesApiCoApplicantInfo(components, logger);
          case 'property-type':
            return getOptionsForLegacyPagesApiPropertyTypeComponent(components, logger);
          case 'roof-shape':
            return getOptionsForLegacyPagesApiRoofShapeComponent(components, logger);
          case 'roof-year':
            return getValidationsForLegacyPagesAPIRoofYearComponent(components, logger);
          case 'year-built':
            return getOptionsForLegacyPagesApiYearBuiltComponent(components, logger);
          case 'construction-type':
            return getOptionsForLegacyPagesApiConstructionTypeComponent(components, logger);
          case 'exterior-wall':
            return getOptionsForLegacyPagesApiExteriorWallComponent(components, logger);
          case 'property-information':
            return getOptionsForLegacyPagesApiPropertyInformationComponent(components, logger);
          case 'foundation':
            return getOptionsForLegacyPagesApiFoundationTypeComponent(components, logger);
          case 'insurance-expiration':
            return getComponentsForLegacyPagesApiInsuranceExpiration(components, logger);
          default:
            logger.error({ message: `unknown formKey ${formKey}`, context: 'getLegacyOptionsForForm', priority: 'P3' });
            return undefined;
        }
      },
    })),
  );
}
